/**
 * TimelineListBase is a base class for items that need to be placed on the timeline
 * e.g. Event instances & feeling data points
 */
class TimelineItemList {

	constructor(startTimeProperty, endTimeProperty) {
		this._startTimeProperty = startTimeProperty;
		this._endTimeProperty = endTimeProperty;
		this.init();
	}

	init() {
		this._items = [];
		this._ids = [];
		this._index = -1;
		this._firstInView = 0; // index of first event in view,
		this._lastInView = -1; // index of last event in view

		this._marker = null;
	}

	get items() {
		return this._items;
	}

	get item() {
		return (this.isEmpty || this._index === -1) ? false : this._items[this._index];
	}

	get marked() {
		return (this.isEmpty || this._marker === null) ? false : this._items[this._marker];
	}

	get first() {
		return this.isEmpty ? false : this._items[0];
	}

	get last() {
		return this.isEmpty ? false : this._items[this.count - 1];
	}

	get hasPrev() {
		return 0 < this._index;
	}

	get prev() {
        return this.hasPrev ? this._items[this._index - 1] : false;
	}
	
	get hasNext() {
		return this._index < this._ids.length - 1;
	}

	get next() {
        return this.hasNext ? this._items[this._index + 1] : false;
	}

    get index() {
        return this._index;
    }

	get count() {
        return this._ids.length;
    }

	get isEmpty() {
		return this.count == 0;
	}

	get isNotEmpty() {
		return 0 < this.count;
	}

	get hasMark() {
		return this._marker !== null;
	}

	add(item) {
		this.seekTime(item[this._startTimeProperty]);

		this._items.splice(this._index, 0, item);
		this._ids.splice(this._index, 0, item.id);
		if (this._index < this._firstInView) this._firstInView++;
		return item;
	}
 
	remove(item) {
		this.seekTime(item[this._startTimeProperty]);
		if (this.item === item) return this._remove(item);
		while (this.hasPrev && this.prev[this._startTimeProperty] == item[this._startTimeProperty]) {
			if (this.movePrev() === item) return this._remove(item);
		}
		while (this.hasNext && this.next[this._startTimeProperty] == item[this._startTimeProperty]) {
			if (this.moveNext() === item) return this._remove(item);
		}

		return item;
	}
	
	_remove(item) {
		if (this._index < this._firstInView) this._firstInView--;
		if (this._index <= this._lastInView) this._lastInView--;
		
		this._ids.splice(this._index,1);
		this._items.splice(this._index,1)[0];

		if (this._ids.length <= this._index) this._index = this._ids.length - 1;
		return item;
	}

	pop() {
		this._ids.pop();
		return this._items.pop();
	}

	exists(item) {
		this.seekTime(item[this._startTimeProperty]);
		return this.item === item;
	}

	reset() {
		this._index = -1;
	}

    moveToPosition(position) {
        if (position < 0 || this._ids.length <= position) return false;
        this._index = position;
        return true;
    }

    seekId(id) {
		let index = this._ids.indexOf(id);
		if (index === -1) return false;
		this._index = index;
		return true;
    }

    seekTime(time) {
        if (this.isEmpty) {
			this._index = 0;
			return;
		};

        while (0 < this._index && time <= this.prev[this._startTimeProperty]) this._index--;
        while (this._index < this._ids.length && this.item[this._startTimeProperty] < time) this._index++;
    }

	moveToFirstInView() {
		if (this._lastInView < this._firstInView) return false;
		return this.moveToPosition(this._firstInView);
	}

	moveToNextInView() {
		return this.moveToPosition(++this._index) && this._index <= this._lastInView;
	}

    movePrev() {
        if (this.hasPrev) return this._items[--this._index];
        return false;
    }

    moveNext() {
        if (this.hasNext) return this._items[++this._index];
        return false;
    }

	moveLast() {
		if (this.isEmpty) return false;
		this._index = this.count - 1;
		return this.item; 
	}

	mark(item) {
		if (item === null) {
			this._marker = null;
			return;
		}

		this.seekTime(item[this._startTimeProperty]);
		this._marker = this._index;
	}

	withMarked(func) {
		if (typeof(func) !== 'function' || this.isEmpty || !this.marked) return;

		func(this.marked);
	}

	forEach(func) {
		if (typeof(func) !== 'function' || this.isEmpty) return;

		for (let i = 0; i < this.count; i++) {
			func(this._items[i]);
		}
	}

	forEachInView(func) {
		if (typeof(func) !== 'function' || this.isEmpty) return;

		for (let i = this._firstInView; i <= this._lastInView; i++) {
			func(this._items[i]);
		}
		
	}

	forEachAfter(func) {
		if (typeof(func) !== 'function' || this.isEmpty) return;

		for (let i = this._marker; i < this.count; i++) {
			func(this._items[i]);
		}
	}

	updateInView(viewStartTime, viewEndTime) {
		if (this.isEmpty) return;

		while (
			this._lastInView < this._items.length - 1
			&& this._items[this._lastInView + 1][this._startTimeProperty] < viewEndTime
		) {
			this._items[++this._lastInView].visible = true;
		}

		while (
			0 < this._firstInView 
			&& viewStartTime < this._items[this._firstInView - 1][this._endTimeProperty]
		) {
			this._items[--this._firstInView].visible = true;
		}

		while (
			this._firstInView < this._items.length
			&& this._items[this._firstInView][this._endTimeProperty] < viewStartTime
		) {
			this._items[this._firstInView++].visible = false;
		}

		while (
			-1 < this._lastInView
			&& viewEndTime < this._items[this._lastInView][this._startTimeProperty]
		) {
			this._items[this._lastInView--].visible = false;
		}
	}
}